// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import toggleLeaseAgreement from "@guestsupply/App/Handlers/XNG/CartLeaseAgreement";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    cart?: Cart;
    extendedStyles?: CartDiscountPercentDisplayStyles;
}

interface Tier {
    tierId: number;
    discountPercent: number;
}

interface DiscountResult {
    discountPercent: number;
    nextTierDiscountPercent: number;
    amountNeededForNextTier: number;
    tiers: Tier[];
}

const mapDispatchToProps = {
    toggleLeaseAgreement,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CartDiscountPercentDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    wrapper?: InjectableCss;
}

export const cartDiscountPercentDisplayStyles: CartDiscountPercentDisplayStyles = {};

const CartDiscountPercentDisplay = ({ isCartEmpty = false, cart, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartDiscountPercentDisplayStyles, extendedStyles));
    const [discountResult, setDiscountResult] = React.useState<DiscountResult | null>(null);

    useEffect(() => {
        if (cart?.properties?.discountResult) {
            setDiscountResult(JSON.parse(cart.properties.discountResult));
        }
    }, [cart]);


    if (!cart || isCartEmpty || !discountResult) {
        return null;
    }

    const showOtherDicounts = discountResult?.tiers?.length > 0;

    return (
        <StyledWrapper {...styles.wrapper}>
            {(!!Number(discountResult.discountPercent) ||
                !!Number(discountResult.nextTierDiscountPercent) ||
                !!Number(discountResult.amountNeededForNextTier)) && (
                <GridContainer
                    {...styles.container}
                    data-test-selector={`discountPercent_${cart.id}`}
                    className="discount-summary"
                >
                    {!!Number(discountResult.discountPercent) && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>{translate("Discount Percent")}</Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.subtotalValue}
                                    data-test-selector="discountPercent_discountPercent"
                                >
                                    {discountResult.discountPercent}%
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {!!Number(discountResult.nextTierDiscountPercent) && showOtherDicounts && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Next Tier Discount Percent")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.subtotalValue}
                                    data-test-selector="discountPercent_nextTierDiscountPercent"
                                >
                                    {discountResult.nextTierDiscountPercent}%
                                </Typography>
                            </GridItem>
                        </>
                    )}
                    {!!Number(discountResult.amountNeededForNextTier) && showOtherDicounts && (
                        <>
                            <GridItem {...styles.labelGridItem}>
                                <Typography {...styles.subtotalLabel}>
                                    {translate("Amount Needed For Next Tier")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.valueGridItem}>
                                <Typography
                                    {...styles.subtotalValue}
                                    data-test-selector="shippingSummary_amountNeededForNextTier"
                                >
                                    ${discountResult.amountNeededForNextTier}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            )}
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(CartDiscountPercentDisplay);
